import { TermsAndConditionsModal } from '@components/Modal/TermsAndConditionsModal';
import { SubscriptionFooter } from '@components/SubscriptionFooter';
import { useToast } from '@contexts/Toast';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import { errorMessage, responseTexts } from '@helpers/Constants';
import { CheckCircleFilledIcon, StarIcon } from '@helpers/Constants/Icons';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { SubscriptionPlanProps } from '@helpers/Models';
import { handleDateFormat } from '@services/OrderCreationService';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const PlansViewDetails = ({
  isFromAppSettings,
  subId,
}: {
  isFromAppSettings?: boolean;
  subId?: number;
}) => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();

  const [verifyPromoCode, setverifyPromoCode] = useState<string>('');
  const [promoCode, setPromoCode] = useState<string>('');
  const [showVerifySuccess, setShowVerifySuccess] = useState<boolean>(false);
  const [promoCodeSuccessMessage, setPromoCodeSuccessMessage] = useState<{
    code: string;
    description: string;
  }>({
    code: '',
    description: '',
  });
  const [errors, setErrors] = useState<string>('');
  const [subscriptions, setSubscriptions] = useState<SubscriptionPlanProps>({
    id: 0,
    planName: '',
    description: '',
    price: 0,
    billingCycle: '',
    trialDays: 0,
    trialMaxAnimals: 0,
    isActive: 0,
  });
  const [billingDate, setBillingDate] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  /**
   * fetchSubscriptions
   *
   * Fetches subscription details for the current user.
   *
   * Workflow:
   * - Sends a GET request to retrieve subscription data.
   * - Extracts and sets the primary subscription details from the response.
   * - Updates the billing date in the state.
   *
   * Error Handling:
   * - Displays an error toast message if the API request fails.
   *
   * Dependencies:
   * - Axios for API calls
   * - ApiEndpoints.FETCH_SUBSCRIPTION for endpoint reference
   * - showToastMessage for user notifications
   * - setSubscriptions and setBillingDate for state management
   *
   * @async
   * @function fetchSubscriptions
   * @returns {Promise<void>}
   */
  const fetchSubscriptions = async (): Promise<void> => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_SUBSCRIPTION);
      setSubscriptions(response.data.data.subscriptions[0]);
      setBillingDate(response.data.data.billingDate);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * handleApply
   *
   * Handles the application and verification of a promo code.
   *
   * Workflow:
   * - Checks if a promo code is entered; exits early if empty.
   * - Clears any existing error messages.
   * - Sends a request to verify the promo code via API.
   * - On success:
   *    - Displays a success toast message.
   *    - Stores the promo code details (code and description).
   *    - Updates UI state to reflect successful verification.
   * - On failure:
   *    - Sets an error message indicating an invalid promo code.
   *    - Resets the success state.
   *
   * Dependencies:
   * - Axios for API calls
   * - ApiEndpoints.VERIFY_PROMOCODE for endpoint reference
   * - showToastMessage for user notifications
   * - setErrors, setPromoCodeSuccessMessage, setShowVerifySuccess for state management
   *
   * @async
   * @function handleApply
   * @returns {Promise<void>}
   */
  const handleApply = async (): Promise<void> => {
    if (verifyPromoCode == '') return;
    // Removing the previous error, if its exists
    setErrors('');

    try {
      const response = await Axios.post(ApiEndpoints.VERIFY_PROMOCODE, {
        promoCode: verifyPromoCode,
      });
      // Set the verified promocode for the subscription
      setPromoCode(verifyPromoCode);
      showToastMessage(response.data.message, 'success');
      setPromoCodeSuccessMessage({
        code: response.data.data.code,
        description: response.data.data.description,
      });
      setShowVerifySuccess(true);
    } catch (error: any) {
      setErrors(error.response.data.message);
      setShowVerifySuccess(false);
    }
  };

  /**
   * Reactivates a suspended or inactive subscription.
   *
   * @async
   * @function reactivateSubscription
   * @returns {Promise<void>}
   *
   * @remarks
   * - Sends a request to the backend with the subscription ID and optional promo code.
   * - On success:
   *   - Updates the subscription status in `localStorage` to ACTIVE.
   *   - Navigates the user to the subscription reactivation success page.
   *   - Displays a success toast message.
   * - On failure:
   *   - Displays an error toast message for invalid promo code.
   *   - Resets the promo code verification success state.
   */
  const reactivateSubscription = async (): Promise<void> => {
    try {
      const payload = {
        id: subId,
        promoCode: promoCode,
      };

      const response = await Axios.post(
        ApiEndpoints.REACTIVATE_SUBSCRIPTION,
        payload,
      );
      // Set the subscription status to 2 (Active)
      localStorage.setItem('subscriptionStatus', '2');
      // Set the subscriptionActiveFlag status to 1 to allow users to navigate through modules
      localStorage.setItem('subscriptionActiveFlag', '1');

      // Navigating to the payment page
      navigate(FrontendRoutes.SUBSCRIPTION_REACTIVATE_SUCCESS);

      showToastMessage(response.data.message, 'success');
    } catch {
      showToastMessage(errorMessage.INVALID_PROMOCODE, 'error');
      setShowVerifySuccess(false);
    }
  };

  /**
   * handleStartSubscriptionOrTrails
   *
   * Initiates the subscription or trial flow for the selected plan.
   *
   * Workflow:
   * - Validates whether the user has accepted the terms and conditions.
   * - Prepares the payload with subscription ID and trial flag.
   * - Calls the API to create a subscription and generate a checkout session.
   * - Stores the checkout session payload and session ID in localStorage for recovery (e.g., payment failure).
   * - Redirects the user to the Stripe checkout/payment page.
   * - Displays appropriate success or error messages via toast notifications.
   *
   * Error Handling:
   * - Shows an error message if terms are not accepted.
   * - Handles API failures (e.g., invalid promo code) and resets verification state.
   *
   * Dependencies:
   * - Axios for API calls
   * - ApiEndpoints.CREATE_SUBSCRIPTION for endpoint reference
   * - showToastMessage for user notifications
   * - localStorage for temporary session persistence
   *
   * @async
   * @function handleStartSubscriptionOrTrails
   * @returns {Promise<void>}
   */
  const handleStartSubscriptionOrTrails = async (): Promise<void> => {
    if (!termsAccepted) {
      showToastMessage(errorMessage.TERMS_AND_CONDITION_FAILURE, 'error');
      return;
    }

    if (isFromAppSettings) {
      reactivateSubscription();
      return;
    } else {
      try {
        const payload = {
          subscriptionId: subscriptions.id,
          isTrial: 0,
          promoCode: promoCode ?? '',
        };

        const response = await Axios.post(
          ApiEndpoints.CREATE_SUBSCRIPTION,
          payload,
        );

        // Storing the create checkout session data for future use - if the payment fails
        localStorage.setItem('createCheckoutSession', JSON.stringify(payload));

        // Navigating to the payment page
        window.location.href = response.data.data.checkoutUrl;

        localStorage.setItem('sessionId', response.data.data.sessionId);
        showToastMessage(responseTexts.REDIRECTING_PAYMENT, 'success');
      } catch (error: any) {
        showToastMessage(error.error.message, 'error');
        setShowVerifySuccess(false);
      }
    }
  };

  const handleTermsLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsTermsModalOpen(true);
  };

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setIsTermsModalOpen(false);
  };

  const handleTermsDecline = () => {
    setTermsAccepted(false);
    setIsTermsModalOpen(false);
  };

  const handleTermsModalClose = () => {
    setIsTermsModalOpen(false);
  };

  /**
   * Handles input change events for form elements.
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} event - The change event triggered by the input or select element.
   *
   * @remarks
   * - Specifically handles the "terms" checkbox input.
   * - Updates the `termsAccepted` state based on whether the checkbox is checked.
   */
  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const target = event.target;

    if (target.name === 'terms') {
      setTermsAccepted((target as HTMLInputElement).checked);
    }
  };

  return (
    <div className="p-3">
      {/* Title */}
      <div className="mt-6 p-2 bg-[#EFF5F4]">
        <div className="flex flex-col gap-2 text-gray-700">
          <div className="flex flex-col items-center justify-center p-2">
            <span className="text-[#292D32] text-5xl ml-2 font-bold">
              Start Your Subscription
            </span>
            <span className="mt-5">
              Set up your payment method. You&apos;ll only be charged based on
              your monthly usage.
            </span>
          </div>
        </div>
      </div>
      <div className="p-3 w-full max-w-[1100px] mx-auto">
        <div className="mt-3 mb-6 flex flex-col md:flex-row sm:flex-row justify-center gap-8 items-start">
          {/* Div - 1 Plan card */}
          <div
            className="p-3 w-full md:flex-1 items-stretch"
            style={{
              borderWidth: '4px 2px 2px 2px',
              borderStyle: 'solid',
              borderImage:
                'linear-gradient(224.86deg, #73CCD8 4.87%, #2B6B9F 96.04%) 1',
            }}
          >
            {/* Plan type badge */}
            <div className="p-2 bg-[#EFF5F4] flex flex-row justify-center">
              <span className="mt-1">
                {/* Star Icon */}
                <StarIcon />
              </span>
              <span className="ml-2 text-[#292D32] font-bold">
                SUBSCRIPTION SELECTED
              </span>
            </div>

            {/* Subscription name */}
            <div className="mt-3 text-[#292D32] flex flex-col items-center">
              <span className="font-extrabold text-xl">
                {subscriptions.planName}
              </span>
              <span className="">{subscriptions.description}</span>
            </div>
            {/* Price per animal */}
            <div className="text-[#292D32] flex flex-col items-center">
              <div className="mt-1">
                <span className="font-bold text-2xl">$</span>
                <span className="text-4xl"> {subscriptions.price}</span>
                <span className="mb-3 text-[#6C757D]"> / animal</span>
              </div>
              <span className="mt-3 text-[#6C757D]">
                {subscriptions.billingCycle}
              </span>
            </div>
          </div>
          {/* Div -2 Plan details */}
          <div className="p-3 w-full md:flex-1 border border-[#E8E8E8] rounded-lg">
            {/* Promo code apply */}
            <span className="text-bold font-medium text-[#1A1A1A]">
              Enter Promo Code
            </span>
            <div className="mt-1">
              <div className="flex items-center rounded-md overflow-hidden">
                <div
                  className={`flex items-center flex-1 pl-3 border ${errors ? 'border-[#EC221F]' : 'border-gray-600'}  mr-2`}
                >
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 13C10.2833 13 10.5208 12.9042 10.7125 12.7125C10.9042 12.5208 11 12.2833 11 12C11 11.7167 10.9042 11.4792 10.7125 11.2875C10.5208 11.0958 10.2833 11 10 11C9.71667 11 9.47917 11.0958 9.2875 11.2875C9.09583 11.4792 9 11.7167 9 12C9 12.2833 9.09583 12.5208 9.2875 12.7125C9.47917 12.9042 9.71667 13 10 13ZM10 9C10.2833 9 10.5208 8.90417 10.7125 8.7125C10.9042 8.52083 11 8.28333 11 8C11 7.71667 10.9042 7.47917 10.7125 7.2875C10.5208 7.09583 10.2833 7 10 7C9.71667 7 9.47917 7.09583 9.2875 7.2875C9.09583 7.47917 9 7.71667 9 8C9 8.28333 9.09583 8.52083 9.2875 8.7125C9.47917 8.90417 9.71667 9 10 9ZM10 5C10.2833 5 10.5208 4.90417 10.7125 4.7125C10.9042 4.52083 11 4.28333 11 4C11 3.71667 10.9042 3.47917 10.7125 3.2875C10.5208 3.09583 10.2833 3 10 3C9.71667 3 9.47917 3.09583 9.2875 3.2875C9.09583 3.47917 9 3.71667 9 4C9 4.28333 9.09583 4.52083 9.2875 4.7125C9.47917 4.90417 9.71667 5 10 5ZM18 16H2C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V10C0.55 10 1.02083 9.80417 1.4125 9.4125C1.80417 9.02083 2 8.55 2 8C2 7.45 1.80417 6.97917 1.4125 6.5875C1.02083 6.19583 0.55 6 0 6V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V6C19.45 6 18.9792 6.19583 18.5875 6.5875C18.1958 6.97917 18 7.45 18 8C18 8.55 18.1958 9.02083 18.5875 9.4125C18.9792 9.80417 19.45 10 20 10V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16ZM18 14V11.45C17.3833 11.0833 16.8958 10.5958 16.5375 9.9875C16.1792 9.37917 16 8.71667 16 8C16 7.28333 16.1792 6.62083 16.5375 6.0125C16.8958 5.40417 17.3833 4.91667 18 4.55V2H2V4.55C2.61667 4.91667 3.10417 5.40417 3.4625 6.0125C3.82083 6.62083 4 7.28333 4 8C4 8.71667 3.82083 9.37917 3.4625 9.9875C3.10417 10.5958 2.61667 11.0833 2 11.45V14H18Z"
                      fill="#6F6F6F"
                    />
                  </svg>
                  <input
                    type="text"
                    value={verifyPromoCode}
                    onChange={(e) => setverifyPromoCode(e.target.value)}
                    placeholder="Enter promo code"
                    className="w-full py-2 px-2 focus:outline-none rounded-md"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleApply}
                  className="px-4 py-3 bg-primary text-white text-sm font-medium hover:bg-dark-primary transition rounded-md"
                >
                  Apply
                </button>
              </div>
              {errors != '' && (
                <p className="text-sm text-[#DF1A0C] mt-3">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> {errors}
                </p>
              )}
            </div>
            {/* Badge for applied promo code */}
            {showVerifySuccess && (
              <div className="bg-[#DBB145] text-white mt-5 p-4 rounded-md flex justify-between items-start gap-4">
                <div>
                  <div className="flex flex-row gap-2 items-center">
                    <span>
                      <svg
                        width="20"
                        height="16"
                        viewBox="0 0 20 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 13C10.2833 13 10.5208 12.9042 10.7125 12.7125C10.9042 12.5208 11 12.2833 11 12C11 11.7167 10.9042 11.4792 10.7125 11.2875C10.5208 11.0958 10.2833 11 10 11C9.71667 11 9.47917 11.0958 9.2875 11.2875C9.09583 11.4792 9 11.7167 9 12C9 12.2833 9.09583 12.5208 9.2875 12.7125C9.47917 12.9042 9.71667 13 10 13ZM10 9C10.2833 9 10.5208 8.90417 10.7125 8.7125C10.9042 8.52083 11 8.28333 11 8C11 7.71667 10.9042 7.47917 10.7125 7.2875C10.5208 7.09583 10.2833 7 10 7C9.71667 7 9.47917 7.09583 9.2875 7.2875C9.09583 7.47917 9 7.71667 9 8C9 8.28333 9.09583 8.52083 9.2875 8.7125C9.47917 8.90417 9.71667 9 10 9ZM10 5C10.2833 5 10.5208 4.90417 10.7125 4.7125C10.9042 4.52083 11 4.28333 11 4C11 3.71667 10.9042 3.47917 10.7125 3.2875C10.5208 3.09583 10.2833 3 10 3C9.71667 3 9.47917 3.09583 9.2875 3.2875C9.09583 3.47917 9 3.71667 9 4C9 4.28333 9.09583 4.52083 9.2875 4.7125C9.47917 4.90417 9.71667 5 10 5ZM18 16H2C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V10C0.55 10 1.02083 9.80417 1.4125 9.4125C1.80417 9.02083 2 8.55 2 8C2 7.45 1.80417 6.97917 1.4125 6.5875C1.02083 6.19583 0.55 6 0 6V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V6C19.45 6 18.9792 6.19583 18.5875 6.5875C18.1958 6.97917 18 7.45 18 8C18 8.55 18.1958 9.02083 18.5875 9.4125C18.9792 9.80417 19.45 10 20 10V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16ZM18 14V11.45C17.3833 11.0833 16.8958 10.5958 16.5375 9.9875C16.1792 9.37917 16 8.71667 16 8C16 7.28333 16.1792 6.62083 16.5375 6.0125C16.8958 5.40417 17.3833 4.91667 18 4.55V2H2V4.55C2.61667 4.91667 3.10417 5.40417 3.4625 6.0125C3.82083 6.62083 4 7.28333 4 8C4 8.71667 3.82083 9.37917 3.4625 9.9875C3.10417 10.5958 2.61667 11.0833 2 11.45V14H18Z"
                          fill="white"
                        />
                      </svg>
                    </span>
                    <span className="text-lg">Promo Code Applied </span>
                  </div>
                  <div className="font-bold pl-6 text-lg">
                    {promoCodeSuccessMessage.code} -{' '}
                    <span className="uppercase">
                      {promoCodeSuccessMessage.description}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="hover:opacity-75 transition-opacity"
                  onClick={() => {
                    /* handle close */
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 5L5 15M5 5L15 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}

            {/* Today - Trial starts */}
            <div className="flex flex-row mt-3">
              <div className="flex flex-col w-full">
                <div className="flex flex-row justify-between gap-3 mt-3 pr-2">
                  <span className="text-[#6C757D] font-medium">
                    Billing Type
                  </span>
                  <span className="text-[#1A1A1A] font-bold">
                    {subscriptions.billingCycle}
                  </span>
                </div>

                <div className="flex flex-row justify-between gap-3 mt-6 pr-2">
                  <span className="text-[#6C757D] font-medium">
                    First Billing Date
                  </span>
                  <span className="text-[#1A1A1A] font-bold">
                    {handleDateFormat(billingDate)}
                  </span>
                </div>
                <div className="flex flex-row justify-between gap-3 mt-6 pr-2">
                  <span className="text-[#6C757D] font-medium">
                    Payment Method
                  </span>
                  <span className="text-[#1A1A1A] font-bold">Auto Pay</span>
                </div>
              </div>
            </div>
            {/* Billing info */}
            <div className="flex flex-row bg-[#FEF3C7] mt-5 p-3">
              <span className="mt-1">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9 15H11V9H9V15ZM10.7125 6.7125C10.9042 6.52083 11 6.28333 11 6C11 5.71667 10.9042 5.47917 10.7125 5.2875C10.5208 5.09583 10.2833 5 10 5C9.71667 5 9.47917 5.09583 9.2875 5.2875C9.09583 5.47917 9 5.71667 9 6C9 6.28333 9.09583 6.52083 9.2875 6.7125C9.47917 6.90417 9.71667 7 10 7C10.2833 7 10.5208 6.90417 10.7125 6.7125ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z"
                    fill="#92400E"
                  />
                </svg>
              </span>
              <span className="ml-2 text-[#92400E] font-medium">
                {
                  "You'll be billed at the end of each month based on the number of animals processed. No upfront payment required!"
                }
              </span>
            </div>

            <div className="mt-3 flex flex-row text-sm p-2">
              {/* Tick icon */}
              <span className="mt-1">
                <CheckCircleFilledIcon width={18} height={18} fill="#004834" />
              </span>
              <span className="ml-3 text-[#495057]">
                Monthly post-paid billing
              </span>
            </div>
            <div className="flex flex-row text-sm p-2">
              {/* Tick icon */}
              <span className="mt-1">
                <CheckCircleFilledIcon width={18} height={18} fill="#004834" />
              </span>
              <span className="ml-3 text-[#495057]">
                Advanced analytics & reports
              </span>
            </div>
            <div className="flex flex-row text-sm p-2">
              {/* Tick icon */}
              <span className="mt-1">
                <CheckCircleFilledIcon width={18} height={18} fill="#004834" />
              </span>
              <span className="ml-3 text-[#495057]">
                Priority customer support
              </span>
            </div>
            <div className="flex flex-row text-sm p-2">
              {/* Tick icon */}
              <span className="mt-1">
                <CheckCircleFilledIcon width={18} height={18} fill="#004834" />
              </span>
              <span className="ml-3 text-[#495057]">Inventory management</span>
            </div>
            <div className="flex flex-row text-sm p-2">
              {/* Tick icon */}
              <span className="mt-1">
                <CheckCircleFilledIcon width={18} height={18} fill="#004834" />
              </span>
              <span className="ml-3 text-[#495057]">
                Custom branding options
              </span>
            </div>

            {/* Terms and conditions */}
            <div className="bg-[#F8F9FA] p-2 mt-5">
              <div className="mb-3 flex items-center">
                <input
                  data-testid="termscondition"
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={handleInputChange}
                  className="h-4 w-4 shrink-0 accent-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="terms" className="ml-3 block">
                  <span className="font-medium text-[#1C1B1F]">I accept</span>{' '}
                  <button
                    type="button"
                    onClick={handleTermsLinkClick}
                    className="text-primary font-bold ml-1"
                  >
                    WGB&apos;s Terms and Conditions
                  </button>
                </label>
              </div>
            </div>

            {/* Button */}
            <div className="mt-3 p-2">
              <button
                type="submit"
                onClick={handleStartSubscriptionOrTrails}
                className="w-full px-6 py-1.5 border border-transparent rounded-md font-bold text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
              >
                <span>
                  <svg
                    width="16"
                    height="21"
                    viewBox="0 0 16 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 17.075L3.975 16.275C3.80833 15.7917 3.65417 15.3 3.5125 14.8C3.37083 14.3 3.25833 13.8 3.175 13.3L2 14.1V17.075ZM6 16.025H10C10.3 15.3583 10.5417 14.5458 10.725 13.5875C10.9083 12.6292 11 11.65 11 10.65C11 9 10.725 7.4375 10.175 5.9625C9.625 4.4875 8.9 3.35 8 2.55C7.1 3.35 6.375 4.4875 5.825 5.9625C5.275 7.4375 5 9 5 10.65C5 11.65 5.09167 12.6292 5.275 13.5875C5.45833 14.5458 5.7 15.3583 6 16.025ZM6.5875 10.4375C6.19583 10.0458 6 9.575 6 9.025C6 8.475 6.19583 8.00417 6.5875 7.6125C6.97917 7.22083 7.45 7.025 8 7.025C8.55 7.025 9.02083 7.22083 9.4125 7.6125C9.80417 8.00417 10 8.475 10 9.025C10 9.575 9.80417 10.0458 9.4125 10.4375C9.02083 10.8292 8.55 11.025 8 11.025C7.45 11.025 6.97917 10.8292 6.5875 10.4375ZM14 17.075V14.1L12.825 13.3C12.7417 13.8 12.6292 14.3 12.4875 14.8C12.3458 15.3 12.1917 15.7917 12.025 16.275L14 17.075ZM8 0C9.65 1.2 10.8958 2.725 11.7375 4.575C12.5792 6.425 13 8.575 13 11.025L15.1 12.425C15.3833 12.6083 15.6042 12.85 15.7625 13.15C15.9208 13.45 16 13.7667 16 14.1V20.025L11.025 18.025H4.975L0 20.025V14.1C0 13.7667 0.0791667 13.45 0.2375 13.15C0.395833 12.85 0.616667 12.6083 0.9 12.425L3 11.025C3 8.575 3.42083 6.425 4.2625 4.575C5.10417 2.725 6.35 1.2 8 0Z"
                      fill="white"
                    />
                  </svg>
                </span>
                <span>Start Subscription</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Footer  */}
      <SubscriptionFooter isFromAppSettings={true} />

      <TermsAndConditionsModal
        isOpen={isTermsModalOpen}
        onClose={handleTermsModalClose}
        onAccept={handleTermsAccept}
        onDecline={handleTermsDecline}
      />
    </div>
  );
};
